import React, {useContext, useEffect} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {EventTriggeringConditionType} from '../../../generated/graphql';
import {useProjectPageContext} from '../../../pages/ProjectPage/ProjectPageContext';
import * as ViewHooks from '../../../state/views/hooks';
import {usePanelBankSectionConfigMaybe} from '../../../state/views/panelBankSectionConfig/hooks';
import {useRampFlagMetricChangeNotifications} from '../../../util/rampFeatureFlags';
import {useActionConfigStore} from '../../Launch/Triggers/CreateTriggerDrawer/ActionStore';
import {AutomationStoreActions} from '../../Launch/Triggers/CreateTriggerDrawer/AutomationStore';
import {getResetStores} from '../../Launch/Triggers/CreateTriggerDrawer/CreateTriggerDrawer';
import {CreateTriggerDrawerLoaded} from '../../Launch/Triggers/CreateTriggerDrawer/CreateTriggerDrawerLoaded';
import {
  CreateTriggerDrawerInit,
  getWorkspacePanelTriggerDrawerInit,
} from '../../Launch/Triggers/CreateTriggerDrawer/EventStore';
import {getSharePanelLink} from '../../Panel/FullScreenPanel/fullScreenUtils';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {LinePlotPanel} from '../Settings/types';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';

const doNothing = () => Promise.resolve();

export const WorkspaceCreateTriggerDrawerWrapper = () => {
  const {closeWorkspaceDrawer, targetPanelRef, targetSectionRef} =
    useWorkspaceDrawerContext();

  const {projectId, projectName, projectEntityName} = useProjectPageContext();

  let metricName = '';
  let panelId = '';
  // There are transition states where this drawer has at least one render pass but
  // targetPanelRef is not set (eg when resizing the window causes the drawer to close)
  const panel = ViewHooks.usePartMaybe(targetPanelRef);
  const panelBankSectionConfig =
    usePanelBankSectionConfigMaybe(targetSectionRef);
  let sharePanelLink: string | undefined = undefined;
  if (
    panelBankSectionConfig != null &&
    panel != null &&
    panel.viewType === 'Run History Line Plot'
  ) {
    panelId = panel.__id__;
    const runHistoryLinePlotPanel = panel as LinePlotPanel;
    metricName = runHistoryLinePlotPanel.config.metrics?.[0] ?? '';
    sharePanelLink = getSharePanelLink(panel, panelBankSectionConfig.name);
  }

  const {doAsyncInit} = useActionConfigStore(useShallow(s => s));
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = selectedAccount?.name ?? '';
  const {
    data: metricChangeNotificationsEnabled,
    loading: metricChangeNotificationsLoading,
  } = useRampFlagMetricChangeNotifications(orgName, projectEntityName);

  useEffect(() => {
    // This useEffect ensures that we reset action config each time the drawer opens
    if (!metricChangeNotificationsLoading) {
      const createTriggerDrawerInit: CreateTriggerDrawerInit = {
        isUpdating: false,
        trigger: undefined,
        ...getWorkspacePanelTriggerDrawerInit(
          projectEntityName,
          projectId,
          projectName,
          metricName,
          EventTriggeringConditionType.RunMetricChange,
          'NotificationTriggeredAction',
          sharePanelLink
        ),
      };
      const resetStores = getResetStores(
        createTriggerDrawerInit,
        metricChangeNotificationsEnabled,
        closeWorkspaceDrawer,
        doAsyncInit
      );
      // note that we are calling an async function here, but not await'ing
      // This is on purpose - later code in this function does not rely on this running to completion.

      resetStores();
      AutomationStoreActions.init(createTriggerDrawerInit, resetStores);
    }
    // we should only trigger this when we are opening, so ignoring changes to the rest of the
    // dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metricChangeNotificationsLoading,
    metricChangeNotificationsEnabled,
    // note that panelId purposefully causes re-run of this hook, since when the user
    // switches panels, we want to reset the trigger drawer.
    panelId,
  ]);

  return (
    <WorkspaceDrawerScroller>
      <div className="mx-20 h-full pb-20">
        <CreateTriggerDrawerLoaded
          onClose={closeWorkspaceDrawer}
          refetchProjectTriggers={
            doNothing /* we don't have a list of triggers visible, so no need to refresh */
          }
          refetchCollectionTriggers={
            doNothing /* we don't have a list of triggers visible, so no need to refresh */
          }
        />
      </div>
    </WorkspaceDrawerScroller>
  );
};
